import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PolicyIcon from '@mui/icons-material/Policy';
import React, { useCallback, useEffect, useState } from 'react';

import { CodePanel, COLORS } from '../argocd/ui';
import type { OpenBaoApi } from './api';

export function PoliciesView({
  api,
  namespace,
  onNotify,
  selected,
  onSelect,
}: {
  api: OpenBaoApi;
  namespace: string;
  onNotify: (severity: 'success' | 'error', message: string) => void;
  /** Open policy name, or empty — owned by the page so it lives in the URL. */
  selected: string;
  onSelect: (name: string) => void;
}) {
  const [policies, setPolicies] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rules, setRules] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorName, setEditorName] = useState('');
  const [editorRules, setEditorRules] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    setPolicies(null);
    try {
      setPolicies(await api.policies(namespace));
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : String(loadError),
      );
    }
  }, [api, namespace]);

  useEffect(() => {
    load();
  }, [load]);

  // The open policy is whatever the address names, so a link opens it
  // directly rather than only a click.
  useEffect(() => {
    if (!selected) {
      setRules(null);
      return undefined;
    }
    let cancelled = false;
    setRules(null);
    api
      .policy(namespace, selected)
      .then(policy => {
        if (!cancelled) setRules(policy.rules);
      })
      .catch(policyError => {
        if (cancelled) return;
        onNotify(
          'error',
          policyError instanceof Error
            ? policyError.message
            : String(policyError),
        );
        onSelect('');
      });
    return () => {
      cancelled = true;
    };
  }, [api, namespace, selected, onNotify, onSelect, reloadTick]);

  const openPolicy = (name: string) => onSelect(name);

  const savePolicy = async () => {
    if (!editorName.trim() || !editorRules.trim()) return;
    setBusy(true);
    try {
      await api.writePolicy(namespace, editorName.trim(), editorRules);
      onNotify('success', `Policy ${editorName.trim()} written`);
      setEditorOpen(false);
      await load();
      if (selected === editorName.trim()) setReloadTick(tick => tick + 1);
    } catch (saveError) {
      onNotify(
        'error',
        saveError instanceof Error ? saveError.message : String(saveError),
      );
    } finally {
      setBusy(false);
    }
  };

  const deletePolicy = async () => {
    if (!deleteCandidate) return;
    setBusy(true);
    try {
      await api.deletePolicy(namespace, deleteCandidate);
      onNotify('success', `Policy ${deleteCandidate} deleted`);
      setDeleteCandidate(null);
      if (selected === deleteCandidate) {
        onSelect('');
        setRules(null);
      }
      await load();
    } catch (deleteError) {
      onNotify(
        'error',
        deleteError instanceof Error
          ? deleteError.message
          : String(deleteError),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ width: 280, flexShrink: 0 }}>
        <Stack direction="row" sx={{ mb: 1, alignItems: 'center' }}>
          <Typography
            sx={{
              flex: 1,
              color: COLORS.muted,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            ACL POLICIES
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreating(true);
              setEditorName('');
              setEditorRules('');
              setEditorOpen(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            New
          </Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {!policies && !error && <CircularProgress size={20} />}
        <TableContainer
          sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2 }}
        >
          <Table size="small">
            <TableBody>
              {(policies ?? []).map(name => (
                <TableRow
                  hover
                  key={name}
                  selected={selected === name}
                  onClick={() => openPolicy(name)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center' }}
                    >
                      <PolicyIcon sx={{ color: COLORS.muted, fontSize: 16 }} />
                      <Typography sx={{ fontSize: 13 }}>{name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 70 }}>
                    {!['root', 'default'].includes(name) && (
                      <Tooltip title="Delete policy">
                        <IconButton
                          size="small"
                          onClick={event => {
                            event.stopPropagation();
                            setDeleteCandidate(name);
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {policies?.length === 0 && (
                <TableRow>
                  <TableCell align="center" sx={{ color: COLORS.muted }}>
                    No policies visible
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {selected ? (
          <>
            <Stack
              direction="row"
              sx={{ mb: 1, alignItems: 'center', gap: 1 }}
            >
              <Typography sx={{ flex: 1, fontSize: 15, fontWeight: 700 }}>
                {selected}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                disabled={rules === null || ['root'].includes(selected)}
                onClick={() => {
                  setCreating(false);
                  setEditorName(selected);
                  setEditorRules(rules ?? '');
                  setEditorOpen(true);
                }}
                sx={{ textTransform: 'none' }}
              >
                Edit
              </Button>
            </Stack>
            {rules === null ? (
              <CircularProgress size={22} />
            ) : (
              <CodePanel value={rules} />
            )}
          </>
        ) : (
          <Typography sx={{ mt: 4, color: COLORS.muted, fontSize: 13 }}>
            Select a policy to view its rules
          </Typography>
        )}
      </Box>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              {creating ? 'New policy' : `Edit ${editorName}`}
            </Box>
            <IconButton onClick={() => setEditorOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {creating && (
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={editorName}
              onChange={event => setEditorName(event.target.value)}
              sx={{ mt: 0.5, mb: 1.5 }}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={14}
            value={editorRules}
            onChange={event => setEditorRules(event.target.value)}
            placeholder={'path "secret/data/*" {\n  capabilities = ["read"]\n}'}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !editorName.trim() || !editorRules.trim()}
            onClick={savePolicy}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteCandidate)}
        onClose={() => setDeleteCandidate(null)}
      >
        <DialogTitle>Delete policy '{deleteCandidate}'?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tokens and groups referencing it lose those permissions
            immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCandidate(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            onClick={deletePolicy}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
